import { ActionTree } from 'vuex'
import { Logger } from '@vue-storefront/core/lib/logger'
import * as types from './mutation-types'
import NetsuiteData, {
  CustomerServiceRequestPayload,
  OrderFilters
} from '../types/NetsuiteData'
import { netsuiteFetch } from '../helpers/api'

const firstOrNull = (data: any) => {
  if (Array.isArray(data)) {
    return data.length ? data[0] : null
  }
  return data || null
}

export const actions: ActionTree<NetsuiteData, any> = {
  async fetchCustomer ({ commit }) {
    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch('/customers/me', undefined, {
        includeEmployee: 1
      })

      const customer = firstOrNull(data)

      commit(types.NETSUITE_CUSTOMER, customer)
      return customer
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite customer')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchOrders ({ commit }, filters: OrderFilters = {}) {
    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const query: Record<string, any> = { ...filters }

      if (Array.isArray(query.status)) {
        query.status = query.status.join(',')
      }

      const data = await netsuiteFetch('/customers/me/orders', { method: 'GET' }, query)
      const orders = Array.isArray(data) ? data : []

      commit(types.NETSUITE_ORDERS, orders)
      return orders
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite orders')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchOrder ({ commit }, etailOrderId: string | number) {
    if (!etailOrderId) {
      throw new Error('etailOrderId is required')
    }

    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch(`/customers/me/orders/${etailOrderId}`)
      const order = firstOrNull(data)
      commit(types.NETSUITE_CURRENT_ORDER, order)
      return order
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite order')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchTracking ({ commit }, etailOrderId: string | number) {
    if (!etailOrderId) {
      throw new Error('etailOrderId is required')
    }

    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch(`/customers/me/orders/${etailOrderId}/tracking`)
      const tracking = Array.isArray(data) ? data : []
      commit(types.NETSUITE_TRACKING, tracking)
      return tracking
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load tracking')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async submitCustomerServiceRequest ({ commit }, payload: CustomerServiceRequestPayload) {
    if (!payload || !payload.message) {
      throw new Error('message is required')
    }

    commit(types.NETSUITE_SUBMITTING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch('/customers/me/customer-service-request', {
        method: 'POST',
        body: JSON.stringify({
          message: payload.message,
          issue: payload.issue,
          subject: payload.subject
        })
      })
      return data
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to submit customer service request')
      throw err
    } finally {
      commit(types.NETSUITE_SUBMITTING, false)
    }
  },

  clear ({ commit }) {
    commit(types.NETSUITE_CLEAR)
  }
}
